<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Language } from '@/types/locale.ts';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { convertUrlToResizer } from '@/utils/utils.js';

// @ts-expect-error has any type
import ImagesLightbox from './ImagesLightbox.vue';

type DesktopGalleryProps = {
  images: Array<{ credit: string | null; lang: Language; name: string; url: string }>;
  hasWidget: boolean;
  containerHeight: string;
  gap: string;
};

const props = defineProps<DesktopGalleryProps>();

const globalConfigurationStore = useGlobalConfigurationStore();
const { currentLocale } = storeToRefs(globalConfigurationStore);

const { t } = useI18n();

const lightboxIndex = ref<number>(0);
const isLightboxOpen = ref<boolean>(false);

const filteredActivityImages = computed(() => {
  if (!props.images?.length)
    return [{ url: '', credit: '', lang: '', name: '' }];
  // DesktopGallery component use a list of 2 to 5 images.
  const images = props.images.filter((image: any) => image.lang === currentLocale.value || !image.lang).slice(0, 4);
  return images;
});

const lightboxImages = computed(() => {
  const images = props.images?.filter((image: any) => image.lang === currentLocale.value || !image.lang);
  return images;
});

const imageCount = computed(() => filteredActivityImages.value?.length ?? 0);

const gridContainerStyle = computed(() => {
  const styles: Record<string, string> = {
    height: props.containerHeight,
    gap: props.gap,
    maxWidth: 'min(100%, 1280)',
  };

  if (imageCount.value > 1) {
    styles.display = 'grid';
    switch (imageCount.value) {
      case 2:
        styles.gridTemplateColumns = 'repeat(2, 1fr)';
        styles.gridTemplateRows = '1fr';
        break;
      case 3:
        styles.gridTemplateColumns = 'repeat(3, 1fr)';
        styles.gridTemplateRows = '1fr';
        break;
      case 4:
      case 5:
        styles.gridTemplateColumns = 'repeat(4, 1fr)';
        styles.gridTemplateRows = 'repeat(2, 1fr)';
        break;
    }
  }

  return styles;
});

function getImageItemStyle(index: number) {
  const count = imageCount.value;
  const style: { gridColumn: string; gridRow: string } = { gridColumn: '', gridRow: '' };

  if (count === 4) {
    switch (index) {
      case 0:
        style.gridColumn = '1 / 3';
        style.gridRow = '1 / 3';
        break;
      case 1:
        style.gridColumn = '3 / 4';
        style.gridRow = '1 / 3';
        break;
      case 2:
        style.gridColumn = '4 / 5';
        style.gridRow = '1 / 2';
        break;
      case 3:
        style.gridColumn = '4 / 5';
        style.gridRow = '2 / 3';
        break;
    }
  }
  else if (count === 5) {
    switch (index) {
      case 0:
        style.gridColumn = '1 / 3';
        style.gridRow = '1 / 3';
        break;
      case 1:
        style.gridColumn = '3 / 4';
        style.gridRow = '1 / 2';
        break;
      case 2:
        style.gridColumn = '3 / 4';
        style.gridRow = '2 / 3';
        break;
      case 3:
        style.gridColumn = '4 / 5';
        style.gridRow = '1 / 2';
        break;
      case 4:
        style.gridColumn = '4 / 5';
        style.gridRow = '2 / 3';
        break;
    }
  }
  return style;
}

function openLightbox() {
  isLightboxOpen.value = true;
}

function openFromImageClick(index: number) {
  lightboxIndex.value = index;
  isLightboxOpen.value = true;
}

function closeLightbox() {
  lightboxIndex.value = 0;
  isLightboxOpen.value = false;
}

function seeAvailabilities() {
  isLightboxOpen.value = false;
  const component = document.querySelector('yb-details');
  const widget = component?.shadowRoot?.getElementById('cta_scroll_target');
  if (widget) {
    widget?.scrollIntoView({ behavior: 'smooth' });
  }
}
</script>

<template>
  <section v-if="imageCount < 2" class="section-container" aria-label="visual presentation">
    <div
      class="image-container"
      :style="gridContainerStyle"
    >
      <div
        class="image-background"
        :style="{ backgroundImage: `url(${convertUrlToResizer(filteredActivityImages?.[0]?.url)})` }"
      />
      <div
        class="image-item"
        @click="openFromImageClick(0)"
      >
        <img :src="convertUrlToResizer(filteredActivityImages?.[0]?.url)" :alt="filteredActivityImages?.[0]?.credit ?? filteredActivityImages?.[0]?.name ?? 'activité'" fetchpriority="high" class="image">
      </div>
      <ImagesLightbox
        :images="lightboxImages" :visible="isLightboxOpen" :start-index="lightboxIndex" :has-widget="props.hasWidget"
        @close="closeLightbox" @check-availability="seeAvailabilities"
      />
      <button type="button" class="lightbox_action" @click="openLightbox">
        {{ t('images.gallery') }}
      </button>
    </div>
  </section>

  <section
    v-else
    class="image-container"
    :style="gridContainerStyle"
  >
    <div
      v-for="(image, index) in filteredActivityImages"
      :key="index"
      class="image-grid-item"
      :style="getImageItemStyle(index)"
      @click="openFromImageClick(index)"
    >
      <img :src="convertUrlToResizer(image.url)" :alt="image.credit ?? image.name ?? 'activité'" class="grid-image">
    </div>
    <ImagesLightbox
      :images="lightboxImages" :visible="isLightboxOpen" :start-index="lightboxIndex" :has-widget="props.hasWidget"
      @close="closeLightbox" @check-availability="seeAvailabilities"
    />
    <button type="button" class="lightbox_action" @click="openLightbox">
      {{ t('images.gallery') }}
    </button>
  </section>
</template>

<style scoped>
.section-container {
  overflow: hidden;
}

.image-container {
  position: relative;
  overflow: hidden;
  border-radius: var(--yb-card-radius);
}

.image-background {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(12px);
  transform: scale(1.05); /* évite les bords blancs dus au blur */
  z-index: 0;
}

.image-grid-item {
  overflow: hidden;
  position: relative;
  cursor: pointer;
}

.grid-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.image-item {
  height: 100%;
  overflow: hidden;
  position: relative;
  z-index: 50;
  cursor: pointer;
}

.image {
  display: block;
  height: 100%;
  width: 50%;
  object-fit: cover;
  object-position: center;
  margin-inline: auto;
}

.lightbox_action {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  outline: none;
  border: 1px solid var(--yb-color-white);
  background: #191b2066;
  color: var(--yb-color-white);
  padding: 0.5rem 1.25rem;
  border-radius: var(--yb-card-radius);
  backdrop-filter: blur(2px);
  cursor: pointer;
}
</style>
